import React, { useState } from 'react';
import { SelectProps } from "@cloudscape-design/components/select";
import Container from "@cloudscape-design/components/container";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import APIOnboardingSection from './APIOnboardingSection';
import APIParametersSection from './APIParametersSection';
import {ApiConfig, defaultState, OnboardingStatus} from './types';
import {AAA_IDENTIFIER_PATTERN, LOOM_ACCESS_TOKEN, RESOURCE_REGEX_PATTERN} from "src/constants";
import { submitOnboardingRequest, pollOnboardingStatus, buildOnboardingJson } from './onboardingApiUtils';

const APIOnboardingForm = () => {
    const [formData, setFormData] = useState<ApiConfig>(defaultState);
    const [superStarNameOptions, setSuperStarNameOptions] = useState<SelectProps.Options>([]);
    const [onboardingStatus, setOnboardingStatus] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [jsonOutput, setJsonOutput] = useState("");
    const [showErrors, setShowErrors] = useState(false);

    const token = localStorage.getItem(LOOM_ACCESS_TOKEN);
    const CLIENT_AUTHENTICATION = true;

    const isFormValid : () => boolean = () => {
        return !!(formData.environment &&
            formData.superStar &&
            formData.aaaIdentifier &&
            AAA_IDENTIFIER_PATTERN.test(formData.aaaIdentifier) &&
            formData.resource && formData.httpMethod?.value &&
            RESOURCE_REGEX_PATTERN.test(formData.resource));
    };

    const generateJson = () => {
        setShowErrors(true);

        if (!isFormValid()) {
            alert("Please fill out all required fields correctly.");
            return;
        }

        const apiJson = buildOnboardingJson(formData, CLIENT_AUTHENTICATION);
        setJsonOutput(apiJson);
    };

    const handleOnboarding = async () => {
        if (!jsonOutput || !token) return;

        setIsLoading(true);
        setOnboardingStatus('');

        const environment = formData.environment?.value?.toLowerCase();

        try {
            const pollingUrl = await submitOnboardingRequest({
                jsonOutput,
                token,
                environment,
            });

            setOnboardingStatus("Request submitted, checking status...");

            pollOnboardingStatus({
                pollingUrl,
                token,
                onUpdate: (status) => {
                    if (status === OnboardingStatus.ACCEPTED) {
                        setOnboardingStatus('Request accepted, waiting for completion...');
                    } else {
                        setOnboardingStatus(`Current Status: ${status}`);
                    }
                },
                onComplete: (finalStatus) => {
                    setIsLoading(false);
                    setOnboardingStatus(
                        finalStatus === OnboardingStatus.SUCCEEDED ? 'Onboarding successful!' : 'Onboarding failed.'
                    );
                },
                onError: (error) => {
                    console.error("Polling error:", error);
                    setIsLoading(false);
                    setOnboardingStatus("Error checking status");
                },
            });
        } catch (error) {
            console.error("Onboarding error:", error);
            setOnboardingStatus("Error in onboarding process");
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={e => e.preventDefault()}>
            <SpaceBetween size="l">
                <APIOnboardingSection
                    formData={formData}
                    setFormData={setFormData}
                    superStarNameOptions={superStarNameOptions}
                    setSuperStarNameOptions={setSuperStarNameOptions}
                    showErrors={showErrors}
                    aaaIdentifierPattern={AAA_IDENTIFIER_PATTERN}
                />

                <APIParametersSection
                    formData={formData}
                    setFormData={setFormData}
                />

                <Container>
                    <button type="submit" onClick={generateJson}>Generate JSON</button>

                    {jsonOutput && (
                        <>
                            <Header variant="h3">Generated JSON:</Header>
                            <pre>{jsonOutput}</pre>

                            <Button
                                variant="primary"
                                onClick={handleOnboarding}
                                loading={isLoading}
                                disabled={isLoading}
                            >
                                Onboard API
                            </Button>
                        </>
                    )}

                    {onboardingStatus && (
                        <StatusIndicator
                            type={
                                onboardingStatus.includes('successful') ? 'success' :
                                    onboardingStatus.includes('failed') ? 'error' :
                                        'in-progress'
                            }
                        >
                            {onboardingStatus}
                        </StatusIndicator>
                    )}
                </Container>
            </SpaceBetween>
        </form>
    );
};

export default APIOnboardingForm;
